import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import AuthShell from "../../components/AuthShell";
import { api } from "../../lib/api";
import { formatApiErrorDetail } from "../../lib/constants";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("Missing or invalid reset link.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/customer/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Choose a new password" subtitle="Set a fresh password for your account.">
      {done ? (
        <div data-testid="reset-success" className="rounded-xl bg-[#DCFCE7] border border-[#BBF7D0] px-4 py-4 text-[#166534]">
          <CheckCircle2 size={22} className="mb-2" />
          <p className="font-semibold">Password reset successful</p>
          <p className="text-sm mt-1">Redirecting you to sign in…</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {error && (
            <div data-testid="reset-error" className="flex items-start gap-2 rounded-xl bg-[#FFF0F0] border border-[#FFD0D0] px-4 py-3 text-sm text-[#991B1B]">
              <AlertCircle size={18} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          <div className="relative">
            <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              data-testid="reset-password-input"
              type={show ? "text" : "password"}
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-[52px] pl-11 pr-11 rounded-xl bg-gray-100 focus:bg-white border border-transparent focus:border-[#228B22] outline-none transition-colors"
            />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
              {show ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <button
            data-testid="reset-submit-button"
            type="submit"
            disabled={loading}
            className="w-full h-[52px] rounded-full bg-[#228B22] hover:bg-[#1A4331] text-white font-semibold transition-colors duration-200 flex items-center justify-center disabled:opacity-60"
          >
            {loading ? <Loader2 size={20} className="animate-spin" /> : "Reset Password"}
          </button>
        </form>
      )}
      <Link to="/login" className="mt-6 inline-block text-sm text-gray-500 hover:text-[#228B22]">
        Back to sign in
      </Link>
    </AuthShell>
  );
}
